// <feature-callouts> — a product-technology explainer.
// Reads `feature-callout` blocks (a hidden data list), shows one callout at a time, and draws an
// SVG connector line + marker from the active callout to its point (x%,y%) on the centre image.
// Desktop: callout docked in a corner. Mobile: image on top, callout centred below, line pointing up.
// Prev/next arrows + swipe cycle; optional loop + autoplay. Connector redraws on resize / first paint.

class FeatureCallouts extends HTMLElement {
  connectedCallback() {
    if (this._init) {
      // editor drag-move (disconnect+reconnect of the SAME element): re-arm the per-connect
      // window listeners + autoplay that disconnectedCallback tears down
      this._bindWin();
      this._startAutoplay();
      this._drawConnector(false);
      return;
    }
    this._init = true;

    this.cfg = this._readJSON('[data-fc-config]') || {};
    this.root = this.closest('.feature-callouts') || this;
    this.refs = {
      inner: this.querySelector('[data-fc-inner]'),
      svg: this.querySelector('[data-fc-svg]'),
      line: this.querySelector('[data-fc-line]'),
      marker: this.querySelector('[data-fc-marker]'),
      stage: this.querySelector('[data-fc-stage]'),
      image: this.querySelector('[data-fc-image]'),
      active: this.querySelector('[data-fc-active]'),
      data: this.querySelector('[data-fc-data]'),
    };
    this.prevBtns = Array.from(this.querySelectorAll('[data-fc-prev]'));
    this.nextBtns = Array.from(this.querySelectorAll('[data-fc-next]'));

    this.callouts = this._parseCallouts();
    this.index = 0;

    if (!this.callouts.length || !this.refs.active) {
      if (this.refs.svg) this.refs.svg.style.display = 'none';
      return;
    }

    this.root.classList.add('feature-callouts--ready');
    if (this.cfg.arrowShape === 'round') this.root.classList.add('feature-callouts--arrows-round');

    this._bind();
    this._show(0, false);
    this._bindImageLoad();
    this._bindWin();
    this._startAutoplay();
  }

  disconnectedCallback() {
    if (this._drawRaf) { cancelAnimationFrame(this._drawRaf); this._drawRaf = null; }
    clearTimeout(this._rt);
    (this._settleTimers || []).forEach(clearTimeout);
    this._settleTimers = [];
    this._stopAutoplay();
    this._unbindWin();
  }

  _bindWin() {
    if (this._winBound) return;
    this._winBound = true;
    if (this._onWinUp) window.addEventListener('pointerup', this._onWinUp);
    if (this._onWinResize) window.addEventListener('resize', this._onWinResize);
    if (this._onWinLoad) window.addEventListener('load', this._onWinLoad);
  }

  _unbindWin() {
    if (!this._winBound) return;
    this._winBound = false;
    if (this._onWinUp) window.removeEventListener('pointerup', this._onWinUp);
    if (this._onWinResize) window.removeEventListener('resize', this._onWinResize);
    if (this._onWinLoad) window.removeEventListener('load', this._onWinLoad);
  }

  _readJSON(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  _parseCallouts() {
    if (!this.refs.data) return [];
    return Array.from(this.refs.data.querySelectorAll('.fc-callout')).map((el) => {
      const x = parseFloat(el.dataset.x);
      const y = parseFloat(el.dataset.y);
      return {
        html: el.innerHTML,
        x: isNaN(x) ? 50 : x,
        y: isNaN(y) ? 50 : y,
        mx: isNaN(parseFloat(el.dataset.mx)) ? (isNaN(x) ? 50 : x) : parseFloat(el.dataset.mx),
        my: isNaN(parseFloat(el.dataset.my)) ? (isNaN(y) ? 50 : y) : parseFloat(el.dataset.my),
        corner: el.dataset.corner || 'bottom-right',
      };
    });
  }

  // ---------- render ----------
  _show(i, animate) {
    const n = this.callouts.length;
    this.index = ((i % n) + n) % n;
    const c = this.callouts[this.index];
    const active = this.refs.active;

    active.innerHTML = c.html;
    active.className = 'feature-callouts__active feature-callouts__active--' + c.corner;
    active.style.opacity = '0';
    requestAnimationFrame(() => {
      active.style.transition = 'opacity 0.3s ease';
      active.style.opacity = '1';
    });

    this._updateArrows();
    this._drawConnector(animate !== false);
  }

  _updateArrows() {
    // when not looping, disable the arrow at each end
    const disable = (btns, off) => {
      const d = !this.cfg.loop && off;
      btns.forEach((b) => {
        // aria-disabled, NOT disabled — disabling the focused button drops keyboard focus
        // to <body> (the product-cards §6.13 convention); clicks are guarded in _bind
        b.setAttribute('aria-disabled', d ? 'true' : 'false');
        b.style.opacity = d ? '0.35' : '';
      });
    };
    disable(this.prevBtns, this.index === 0);
    disable(this.nextBtns, this.index === this.callouts.length - 1);
  }

  _drawConnector(animate) {
    const { inner, svg, line, marker, image, active } = this.refs;
    if (!inner || !svg || !line || !image || !active) return;

    const c = this.callouts[this.index];
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const ir = inner.getBoundingClientRect();
    const imr = image.getBoundingClientRect();
    const ar = active.getBoundingClientRect();

    // guard against pre-layout / lazy-image zero rects — retry next frame, but DEDUPED
    // (one pending rAF, no matter how many entry points fire) and CAPPED (a permanently
    // hidden/removed section must never spin a 60fps loop forever — 2026-08-01 audit)
    if (imr.width < 20 || imr.height < 20 || ar.width < 5) {
      this._drawRetries = (this._drawRetries || 0) + 1;
      if (!this.isConnected || this._drawRetries > 60) return;
      if (this._drawRaf) cancelAnimationFrame(this._drawRaf);
      this._drawRaf = requestAnimationFrame(() => { this._drawRaf = null; this._drawConnector(animate); });
      return;
    }
    this._drawRetries = 0;

    svg.setAttribute('viewBox', '0 0 ' + ir.width + ' ' + ir.height);

    const px = mobile ? c.mx : c.x;
    const py = mobile ? c.my : c.y;
    const tx = imr.left - ir.left + imr.width * (px / 100);
    const ty = imr.top - ir.top + imr.height * (py / 100);

    let sx, sy;
    if (mobile) {
      // callout sits below the image → start from its top-centre, line goes up
      sx = ar.left - ir.left + ar.width / 2;
      sy = ar.top - ir.top - 10;
    } else {
      // corner callout → start from the edge that faces the target, at vertical centre
      const bcx = ar.left - ir.left + ar.width / 2;
      sx = tx >= bcx ? ar.right - ir.left : ar.left - ir.left;
      sy = ar.top - ir.top + ar.height / 2;
    }

    line.setAttribute('d', 'M ' + sx + ' ' + sy + ' L ' + tx + ' ' + ty);

    // marker
    const shape = this.cfg.markerShape || 'square';
    if (shape === 'none') {
      marker.style.display = 'none';
    } else {
      const sz = parseFloat(getComputedStyle(inner).getPropertyValue('--fc-marker-size')) || 12;
      marker.style.display = '';
      marker.setAttribute('x', tx - sz / 2);
      marker.setAttribute('y', ty - sz / 2);
      marker.setAttribute('width', sz);
      marker.setAttribute('height', sz);
      marker.setAttribute('rx', shape === 'dot' ? sz / 2 : 0);
    }

    // line style + draw-in animation
    const dashed = this.cfg.lineStyle === 'dashed';
    const len = Math.hypot(tx - sx, ty - sy);
    if (dashed) {
      line.style.transition = 'none';
      line.style.strokeDasharray = '6 5';
      line.style.strokeDashoffset = '0';
    } else if (animate) {
      line.style.transition = 'none';
      line.style.strokeDasharray = len + ' ' + len;
      line.style.strokeDashoffset = len;
      void svg.getBoundingClientRect().width; // reflow
      requestAnimationFrame(() => {
        line.style.transition = 'stroke-dashoffset 320ms cubic-bezier(0.4,0,0.2,1)';
        line.style.strokeDashoffset = '0';
      });
    } else {
      line.style.transition = 'none';
      line.style.strokeDasharray = 'none';
      line.style.strokeDashoffset = '0';
    }
  }

  // ---------- navigation ----------
  _go(dir) {
    const n = this.callouts.length;
    if (!this.cfg.loop) {
      const ni = this.index + dir;
      if (ni < 0 || ni >= n) return;
    }
    this._show(this.index + dir, true);
  }

  _bind() {
    const guarded = (dir) => () => {
      this._stopAutoplay();
      this._go(dir);
    };
    this.prevBtns.forEach((b) => b.addEventListener('click', (e) => { if (b.getAttribute('aria-disabled') === 'true') return; guarded(-1)(e); }));
    this.nextBtns.forEach((b) => b.addEventListener('click', (e) => { if (b.getAttribute('aria-disabled') === 'true') return; guarded(1)(e); }));

    // swipe on the image + the callout (pointerdown on them, pointerup on window so the gesture
    // completes). Window listeners are stored + (re)bound via _bindWin per connect (§editor lifecycle).
    const start = (e) => { this._sx = e.clientX; this._sy = e.clientY; };
    [this.refs.stage, this.refs.active].forEach((el) => el && el.addEventListener('pointerdown', start));
    this._onWinUp = (e) => {
      if (this._sx == null) return;
      const dx = e.clientX - this._sx;
      const dy = e.clientY - this._sy;
      this._sx = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return; // horizontal only
      this._stopAutoplay();
      this._go(dx < 0 ? 1 : -1);
    };
    this._onWinResize = () => {
      clearTimeout(this._rt);
      this._rt = setTimeout(() => this._drawConnector(false), 120);
    };
  }

  _bindImageLoad() {
    const img = this.refs.image;
    const settle = () => this._drawConnector(false);
    if (img) {
      if (img.complete && img.naturalWidth) settle();
      else img.addEventListener('load', settle);
      if (img.decode) img.decode().then(settle).catch(() => {});
    }
    this._onWinLoad = settle;
    this._settleTimers = [150, 400, 800].map((ms) => setTimeout(settle, ms));
  }

  _startAutoplay() {
    if (!this.cfg.autoplay || this.callouts.length < 2) return;
    this._stopAutoplay();
    const ms = (this.cfg.autoplaySeconds || 5) * 1000;
    this._auto = setInterval(() => this._show(this.index + 1, true), ms);
    if (!this._hoverBound) {
      this._hoverBound = true; // element-lifetime listeners: bind once per element
      this.addEventListener('pointerenter', () => this._stopAutoplay());
      this.addEventListener('focusin', () => this._stopAutoplay());
    }
  }

  _stopAutoplay() {
    if (this._auto) {
      clearInterval(this._auto);
      this._auto = null;
    }
  }
}

if (!customElements.get('feature-callouts')) {
  customElements.define('feature-callouts', FeatureCallouts);
}
